/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useModuleNames.ts
 🗓️ Created: 2025-01-10
 🧩 Description:
    Hook for dynamically loading module names.
    Refactored to use static MENU_DEFINITION and i18n directly
    instead of relying on a removed backend API.

 🔧 Notes:
    - Uses MENU_DEFINITION for local lookup
    - Translates names instantly
    - Prevents 404 API calls

 🔁 Last Updated: 2026-04-10
────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import { useI18n } from './useI18n';
import { MENU_DEFINITION, MenuItemDef } from '../config/menuDefinition';

interface ModuleNameMap {
  [moduleId: string]: string;
}

interface UseModuleNamesReturn {
  moduleNames: ModuleNameMap;
  isLoading: boolean;
  error: string | null;
  getModuleName: (moduleId: string) => string;
  reload: () => Promise<void>;
}

/**
 * Hook to access module names dynamically from the local menu config
 */
export const useModuleNames = (): UseModuleNamesReturn => {
  const { t } = useI18n();

  /**
   * Get human-readable name for a module ID
   */
  const getModuleName = useCallback((moduleId: string): string => {
    // Recursive search to find the matching module titleKey
    const findTitleKey = (items: MenuItemDef[], targetSlug: string): string | null => {
      for (const item of items) {
        if (item.slug === targetSlug) {
          return item.titleKey;
        }
        if (item.children) {
          const found = findTitleKey(item.children, targetSlug);
          if (found) return found;
        }
      }
      return null;
    };

    const titleKey = findTitleKey(MENU_DEFINITION, moduleId);
    
    // If found, translate it. Otherwise fallback to the raw ID.
    return titleKey ? t(titleKey) : moduleId;
  }, [t]);

  /**
   * Reload module names (No-Op in local static architecture)
   */
  const reload = useCallback(async () => {
    // DEPRECATED: Menu is now loaded locally
    return Promise.resolve();
  }, []);

  return {
    moduleNames: {}, // DEPRECATED: Exists for backwards compatibility check but not used
    isLoading: false,
    error: null,
    getModuleName,
    reload
  };
};

/**
 * Preload module names (No-Op in local static architecture)
 */
export const preloadModuleNames = async (): Promise<ModuleNameMap> => {
  return Promise.resolve({});
};

